import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ShieldAlert, Users, Activity, Zap, TrendingUp, AlertTriangle } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { Nav, Footer } from "@/components/Chrome";
import { useAuth } from "@/contexts/AuthContext";
import { getAdminStats, getConfigCheck } from "@/lib/api";

const CONFIG_STATE_COLOR = {
  PRESENT: "#22e58a",
  MISSING: "#ff3b5c",
  FORMAT_INVALID: "#ffb000",
  CONFIGURATION_MISMATCH: "#ff3b5c",
};

const Stat = ({ label, value, sub, Icon, color = "#22d3ee", testid }) => (
  <div className="glass rounded-xl p-5" data-testid={testid}>
    <div className="flex items-center justify-between">
      <p className="font-mono text-[10px] tracking-[0.2em] text-[var(--muted)]">{label}</p>
      {Icon && <Icon size={16} style={{ color }} />}
    </div>
    <p className="font-display font-bold text-3xl mt-2 text-white">{value}</p>
    {sub && <p className="font-mono text-[11px] text-[var(--muted)] mt-0.5">{sub}</p>}
  </div>
);

export default function Admin() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!user) return;
    getAdminStats().then(setStats).catch((e) => {
      if (e?.response?.status === 403 || e?.response?.status === 401) setDenied(true);
      else setDenied(true);
    });
    getConfigCheck().then(setConfig).catch(() => setConfig(null));
  }, [user]);

  const Shell = ({ children }) => (
    <div className="relative text-[var(--text)] min-h-screen">
      <Nav />
      <main className="relative max-w-6xl mx-auto px-6 md:px-10 pt-28 pb-16" data-testid="admin-page">{children}</main>
      <Footer />
    </div>
  );

  if (user === undefined) return <Shell><div className="flex items-center gap-3 font-mono text-sm text-neon-cyan py-20"><Loader2 className="animate-spin" size={18}/> Loading...</div></Shell>;

  if (!user || denied || (user && !user.is_admin)) {
    return (
      <Shell>
        <div className="glass rounded-2xl p-10 text-center border" style={{ borderColor: "rgba(255,59,92,0.35)" }} data-testid="admin-denied">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl border border-neon-red/50 mb-6"><ShieldAlert size={26} className="text-neon-red" /></span>
          <h1 className="font-display text-2xl font-bold text-white">Access denied</h1>
          <p className="font-mono text-sm text-[var(--muted)] mt-2">This area is restricted to the owner account.</p>
          <Link to={user ? "/" : "/login"} className="mt-6 inline-flex font-mono text-sm text-neon-cyan hover:underline">{user ? "Back to app" : "Sign in"}</Link>
        </div>
      </Shell>
    );
  }

  if (!stats) return <Shell><div className="flex items-center gap-3 font-mono text-sm text-neon-cyan py-20"><Loader2 className="animate-spin" size={18}/> Loading analytics...</div></Shell>;

  const u = stats.users, a = stats.analyses, q = stats.quality;
  const fn = stats.funnel || {};
  const bi = stats.intelligence || {};
  const tr = q.avg_token_reduction;
  const trLabel = tr == null ? "—" : tr > 0 ? `−${tr}%` : tr < 0 ? `+${-tr}%` : "~0%";

  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <p className="font-mono text-xs tracking-[0.3em] text-neon-amber mb-2">// OWNER DASHBOARD</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight text-white">Coretext Analytics</h1>
        <p className="font-mono text-sm text-[var(--muted)] mt-2 mb-8">Aggregate product metrics only — no private prompts, tokens, or user content shown here.</p>

        {config && (
          <div className="mb-8" data-testid="admin-config-check">
            <p className="font-mono text-[11px] tracking-[0.2em] text-[var(--muted)] mb-3">
              CONFIGURATION VALIDATION
              <span className="ml-2" style={{ color: config.ok ? "#22e58a" : "#ff3b5c" }}>
                {config.ok ? "ALL OK" : "ATTENTION NEEDED"}
              </span>
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
              {config.checks.map((c) => (
                <div key={c.key} className="glass rounded-lg px-3.5 py-2.5 flex items-center justify-between gap-3" data-testid={`config-${c.key}`}>
                  <span className="font-mono text-[11px] text-white truncate">{c.key}</span>
                  <span className="font-mono text-[9px] tracking-[0.1em] px-2 py-0.5 rounded border shrink-0"
                    style={{ color: CONFIG_STATE_COLOR[c.status] || "#5a6b7a", borderColor: `${CONFIG_STATE_COLOR[c.status] || "#5a6b7a"}55` }}>
                    {c.status}
                  </span>
                </div>
              ))}
            </div>
            <p className="font-mono text-[10px] text-[var(--muted)] mt-2">Shape/type validation only — secret values are never read or displayed.</p>
          </div>
        )}

        <p className="font-mono text-[11px] tracking-[0.2em] text-[var(--muted)] mb-3">AUDIENCE</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat testid="admin-total-users" label="TOTAL ACCOUNTS" value={u.total} Icon={Users} />
          <Stat testid="admin-signups" label="NEW SIGNUPS (7D)" value={u.signups_7d} sub={`${u.signups_30d} in 30d`} Icon={TrendingUp} color="#22e58a" />
          <Stat testid="admin-active" label="ACTIVE USERS (7D)" value={u.active_7d} sub={`${u.active_30d} in 30d`} Icon={Activity} />
          <Stat testid="admin-anon" label="ANON SESSIONS" value={u.anonymous_sessions} sub={`${u.consented} consented`} Icon={Users} color="#ffb000" />
        </div>

        <p className="font-mono text-[11px] tracking-[0.2em] text-[var(--muted)] mb-3">CORETEXT ANALYSES</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat testid="admin-total-analyses" label="TOTAL ANALYSES" value={a.total} Icon={Zap} />
          <Stat testid="admin-audit" label="AUDIT" value={a.audit} sub={`compress ${a.compress} · optimize ${a.optimize}`} Icon={Zap} />
          <Stat testid="admin-free-usage" label="FREE USAGE" value={a.free} sub={`owner ${a.owner} · paid ${a.paid}`} Icon={Activity} color="#22e58a" />
          <Stat testid="admin-errors" label="ERRORS" value={a.errors} Icon={AlertTriangle} color="#ff3b5c" />
        </div>

        <p className="font-mono text-[11px] tracking-[0.2em] text-[var(--muted)] mb-3">ANONYMOUS FREE FUNNEL</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat testid="admin-anon-used-1" label="USED 1 ANALYSIS" value={fn.anon_used_1 ?? 0} sub="sessions" Icon={Users} />
          <Stat testid="admin-anon-used-2" label="USED 2 ANALYSES" value={fn.anon_used_2 ?? 0} sub="sessions" Icon={Users} />
          <Stat testid="admin-anon-used-3" label="USED 3+ (AT CAP)" value={fn.anon_used_3plus ?? 0} sub="reached the free cap" Icon={Users} color="#ffb000" />
          <Stat testid="admin-limit-hits" label="LIMIT BLOCKS" value={fn.limit_hits ?? 0} sub={`${fn.limit_hit_sessions ?? 0} sessions · from now on`} Icon={AlertTriangle} color="#ff3b5c" />
        </div>

        <p className="font-mono text-[11px] tracking-[0.2em] text-[var(--muted)] mb-3">BUILD INTELLIGENCE</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat testid="admin-bi-profiles" label="BI PROFILES" value={bi.profiles ?? 0} sub="users with a profile" Icon={Activity} />
          <Stat testid="admin-bi-authorized" label="BI AUTHORIZED" value={bi.authorized ?? 0} sub="linked a platform" Icon={TrendingUp} color="#22e58a" />
          <Stat testid="admin-avg-readiness" label="AVG READINESS" value={q.avg_readiness_score == null ? "—" : q.avg_readiness_score} sub="raw score" Icon={TrendingUp} />
          <Stat testid="admin-total-audits" label="TOTAL AUDITS" value={q.total_audits ?? 0} sub="scored prompts" Icon={Zap} />
        </div>

        <p className="font-mono text-[11px] tracking-[0.2em] text-[var(--muted)] mb-3">AGGREGATE QUALITY</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat testid="admin-readiness" label="AVG READINESS LIFT" value={q.avg_readiness_improvement == null ? "—" : `+${q.avg_readiness_improvement}`} sub="score → projected" Icon={TrendingUp} color="#22e58a" />
          <Stat testid="admin-token" label="AVG TOKEN CHANGE" value={trLabel} sub={tr != null && tr < 0 ? "added intent" : "compressed"} Icon={Zap} />
          <Stat testid="admin-req" label="REQ RETENTION" value={q.avg_requirement_retention == null ? "—" : `${q.avg_requirement_retention}%`} color="#22e58a" />
          <Stat testid="admin-intent" label="INTENT RETENTION" value={q.avg_intent_retention == null ? "—" : `${q.avg_intent_retention}%`} color="#22e58a" />
        </div>

        <div className="glass rounded-2xl p-6" data-testid="admin-usage-chart">
          <h2 className="font-display text-lg font-bold text-white mb-4">Analyses — last 14 days</h2>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <AreaChart data={stats.usage_by_day} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="uv" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#22d3ee" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="day" tick={{ fill: "#8792a3", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fill: "#8792a3", fontSize: 10 }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0b0d14", border: "1px solid rgba(34,211,238,0.35)", borderRadius: 8, fontFamily: "monospace", fontSize: 12 }} />
                <Area type="monotone" dataKey="count" stroke="#22d3ee" fill="url(#uv)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <p className="font-mono text-[11px] text-[var(--muted)] mt-6">
          Free-account daily limit: <span className="text-white">{stats.config.free_daily_limit ?? "unlimited"}</span> · Anonymous limit: <span className="text-white">{stats.config.anon_limit_24h}/24h</span>
        </p>
      </motion.div>
    </Shell>
  );
}
